/* eslint-disable no-console */
import { useMemo } from "react";
import {
  calcularComisionEdificiosTotal,
  calcularImporteNetoIntermediario,
  calcularPorcentajeComisionIntermediario,
  calculateComission,
  esEdificioComisionFija,
  getConvertedImporte,
  getRateForOpp,
  obtenerNombreIntermediario,
  resolverFechaFactura,
} from "../../../utils";

export function usePublicidadCalcs({
  pais,
  selectedYear,
  monthsToShow,
  currentYear,
  modoCombinado,
  oportunidades,
  oportunidadesChile,
  oportunidadesPeru,
  clientes,
  clientesChile,
  clientesPeru,
  intermediarios,
  intermediariosChile,
  intermediariosPeru,
  edificios,
  edificiosChile,
  edificiosPeru,
  dolarFallback,
  getDolarValue,
  getDolarValueChile,
  getDolarValuePeru,
  users,
  isOportunidadesLoading,
  dolarMensualChile,
  dolarMensualPeru,
}) {
  const valoresMensuales = useMemo(() => {
    if (isOportunidadesLoading) return Array(12).fill(0);

    // Siempre calcula solo el país actual en moneda local
    const valores = Array(12).fill(0);
    oportunidades
      .filter((opp) => !opp.eliminada && opp.facturafecha)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        const facturaYear = facturaDate.getFullYear();
        if (monthIndex >= 0 && monthIndex < monthsToShow && facturaYear === selectedYear) {
          valores[monthIndex] += getConvertedImporte(opp, getRateForOpp(opp, getDolarValue, dolarFallback[pais]));
        }
      });
    return valores;
  }, [oportunidades, monthsToShow, selectedYear, isOportunidadesLoading, pais, dolarFallback, getDolarValue]);

  // Valores de Publicidad AÑO ANTERIOR (para Run Rate que cruza años)
  const valoresMensualesAnoAnterior = useMemo(() => {
    if (isOportunidadesLoading) return Array(12).fill(0);
    const valores = Array(12).fill(0);
    const anoAnterior = selectedYear - 1;
    oportunidades
      .filter((opp) => !opp.eliminada && opp.facturafecha)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        const facturaYear = facturaDate.getFullYear();
        if (monthIndex >= 0 && facturaYear === anoAnterior) {
          valores[monthIndex] += getConvertedImporte(opp, getRateForOpp(opp, getDolarValue, dolarFallback[pais]));
        }
      });
    return valores;
  }, [oportunidades, selectedYear, isOportunidadesLoading, pais, dolarFallback, getDolarValue]);

  // Valores de Publicidad CHILE (siempre en CLP, usando misma lógica)
  const valoresMensualesChile = useMemo(() => {
    if (isOportunidadesLoading) return Array(12).fill(0);
    const valores = Array(12).fill(0);
    oportunidadesChile
      .filter((opp) => !opp.eliminada && opp.facturafecha)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        const facturaYear = facturaDate.getFullYear();
        if (monthIndex >= 0 && monthIndex < monthsToShow && facturaYear === selectedYear) {
          valores[monthIndex] += getConvertedImporte(opp, getRateForOpp(opp, getDolarValueChile, dolarFallback.Chile));
        }
      });
    return valores;
  }, [oportunidadesChile, monthsToShow, selectedYear, isOportunidadesLoading, dolarFallback, getDolarValueChile]);

  // Valores de Publicidad PERÚ (siempre en PEN, usando misma lógica)
  const valoresMensualesPeru = useMemo(() => {
    if (isOportunidadesLoading) return Array(12).fill(0);
    const valores = Array(12).fill(0);
    oportunidadesPeru
      .filter((opp) => !opp.eliminada && opp.facturafecha)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        const facturaYear = facturaDate.getFullYear();
        if (monthIndex >= 0 && monthIndex < monthsToShow && facturaYear === selectedYear) {
          valores[monthIndex] += getConvertedImporte(opp, getRateForOpp(opp, getDolarValuePeru, dolarFallback["Perú"]));
        }
      });
    return valores;
  }, [oportunidadesPeru, monthsToShow, selectedYear, isOportunidadesLoading, dolarFallback, getDolarValuePeru]);

  // Alias para compatibilidad (apunta al país de sesión y al otro país)
  // eslint-disable-next-line no-unused-vars
  const valoresMensualesOtroPais = pais === "Chile" ? valoresMensualesPeru : valoresMensualesChile;

  // Publicidad combinada en USD (usa valores FIJOS de Chile y Perú, no depende de sesión)
  const publicidadMensualesCombinados = useMemo(() => {
    if (!modoCombinado) return null;

    const resultado = Array(12).fill(0);
    for (let i = 0; i < 12; i++) {
      // Chile: CLP -> USD (siempre igual independiente de sesión)
      const valorChile = valoresMensualesChile[i] || 0;
      const dolarChile = dolarMensualChile[i] || dolarFallback.Chile;
      const chileUSD = valorChile / dolarChile;

      // Perú: PEN -> USD (siempre igual independiente de sesión)
      const valorPeru = valoresMensualesPeru[i] || 0;
      const dolarPeru = dolarMensualPeru[i] || dolarFallback["Perú"];
      const peruUSD = valorPeru / dolarPeru;

      resultado[i] = chileUSD + peruUSD;
    }

    return resultado;
  }, [modoCombinado, valoresMensualesChile, valoresMensualesPeru, dolarMensualChile, dolarMensualPeru, pais]);

  const facturasPorMes = useMemo(() => {
    if (isOportunidadesLoading) return Array(12).fill([]);
    const facturas = Array(12)
      .fill()
      .map(() => []);
    const oportunidadesFiltradas = oportunidades.filter(
      (opp) => !opp.eliminada && opp.facturafecha && opp.facturanumero && opp.facturanumero !== "E001-XXX"
    );
    if (oportunidadesFiltradas.length === 0) return facturas;
    const oportunidadesAgrupadas = oportunidadesFiltradas.reduce((acc, opp) => {
      const facturaDate = new Date(opp.facturafecha);
      if (isNaN(facturaDate.getTime())) {
        console.warn(`Invalid facturafecha for oportunidad ${opp.id}: ${opp.facturafecha}`);
        return acc;
      }
      const monthIndex = facturaDate.getMonth();
      const facturaYear = facturaDate.getFullYear();
      if (monthIndex >= 0 && monthIndex < monthsToShow && facturaYear === selectedYear) {
        const key = `${opp.facturanumero}-${opp.facturafecha}`;
        if (!acc[key]) {
          acc[key] = {
            facturanumero: opp.facturanumero,
            cliente: opp.cliente,
            importe: 0,
            importeOriginalMoneda: 0,
            moneda_dolar: !!opp.moneda_dolar,
            monthIndex,
          };
        }
        acc[key].importe += getConvertedImporte(opp, getRateForOpp(opp, getDolarValue, dolarFallback[pais]));
        acc[key].importeOriginalMoneda += parseFloat(opp.importe) || 0;
        if (!opp.moneda_dolar) acc[key].moneda_dolar = false;
      }
      return acc;
    }, {}); // Inicialización explícita
    Object.values(oportunidadesAgrupadas).forEach((group) => {
      if (group && group.monthIndex !== undefined) {
        // Verificación adicional
        facturas[group.monthIndex].push({
          facturanumero: group.facturanumero,
          cliente: group.cliente || "Sin cliente",
          importe: group.importe,
          importeOriginalMoneda: group.importeOriginalMoneda,
          moneda_dolar: group.moneda_dolar,
        });
      }
    });
    return facturas;
  }, [oportunidades, monthsToShow, selectedYear, isOportunidadesLoading, dolarFallback, pais, getDolarValue]);

  // Helper: convierte opp a moneda local para cálculos de comisión
  const oppEnLocal = (opp, getDolarFn, fallback) => {
    if (!opp.moneda_dolar) return opp;
    const rate = getRateForOpp(opp, getDolarFn, fallback);
    return { ...opp, importe: (parseFloat(opp.importe) || 0) * rate, moneda_dolar: false };
  };

  const valoresMensualesComisionAgencia = useMemo(() => {
    const valores = Array(12).fill(0);
    oportunidades
      .filter((opp) => !opp.eliminada && opp.facturafecha && opp.client_id)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        if (monthIndex >= 0 && monthIndex < monthsToShow && facturaDate.getFullYear() === selectedYear) {
          const local = oppEnLocal(opp, getDolarValue, dolarFallback[pais]);
          const neto = calcularImporteNetoIntermediario(
            local,
            Object.values(clientes),
            Object.values(intermediarios),
            pais
          );
          valores[monthIndex] += (parseFloat(local.importe) || 0) - neto;
        }
      });
    return valores;
  }, [oportunidades, clientes, intermediarios, monthsToShow, currentYear, pais, getDolarValue, dolarFallback]);

  // Comisión Agencia AÑO ANTERIOR (para EBITDA Run Rate)
  const valoresMensualesComisionAgenciaAnoAnterior = useMemo(() => {
    const valores = Array(12).fill(0);
    const anoAnterior = selectedYear - 1;
    oportunidades
      .filter((opp) => !opp.eliminada && opp.facturafecha && opp.client_id)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        if (monthIndex >= 0 && facturaDate.getFullYear() === anoAnterior) {
          const local = oppEnLocal(opp, getDolarValue, dolarFallback[pais]);
          const neto = calcularImporteNetoIntermediario(
            local,
            Object.values(clientes),
            Object.values(intermediarios),
            pais
          );
          valores[monthIndex] += (parseFloat(local.importe) || 0) - neto;
        }
      });
    return valores;
  }, [oportunidades, clientes, intermediarios, selectedYear, pais, getDolarValue, dolarFallback]);

  // === COMISIÓN AGENCIA FIJA CHILE ===
  const valoresMensualesComisionAgenciaChile = useMemo(() => {
    const valores = Array(12).fill(0);
    oportunidadesChile
      .filter((opp) => !opp.eliminada && opp.facturafecha && opp.client_id)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        if (monthIndex >= 0 && monthIndex < 12 && facturaDate.getFullYear() === selectedYear) {
          const local = oppEnLocal(opp, getDolarValueChile, dolarFallback.Chile);
          const neto = calcularImporteNetoIntermediario(
            local,
            Object.values(clientesChile),
            Object.values(intermediariosChile),
            "Chile"
          );
          valores[monthIndex] += (parseFloat(local.importe) || 0) - neto;
        }
      });
    return valores;
  }, [oportunidadesChile, clientesChile, intermediariosChile, selectedYear, getDolarValueChile, dolarFallback]);

  // === COMISIÓN AGENCIA FIJA PERÚ ===
  const valoresMensualesComisionAgenciaPeru = useMemo(() => {
    const valores = Array(12).fill(0);
    oportunidadesPeru
      .filter((opp) => !opp.eliminada && opp.facturafecha && opp.client_id)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        if (monthIndex >= 0 && monthIndex < 12 && facturaDate.getFullYear() === selectedYear) {
          const local = oppEnLocal(opp, getDolarValuePeru, dolarFallback["Perú"]);
          const neto = calcularImporteNetoIntermediario(
            local,
            Object.values(clientesPeru),
            Object.values(intermediariosPeru),
            "Perú"
          );
          valores[monthIndex] += (parseFloat(local.importe) || 0) - neto;
        }
      });
    return valores;
  }, [oportunidadesPeru, clientesPeru, intermediariosPeru, selectedYear, getDolarValuePeru, dolarFallback]);

  const comisionesAgenciaPorMes = useMemo(() => {
    const comisiones = Array(12)
      .fill()
      .map(() => []);
    oportunidades
      .filter((opp) => !opp.eliminada && opp.facturafecha && opp.client_id)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        if (monthIndex >= 0 && monthIndex < monthsToShow && facturaDate.getFullYear() === selectedYear) {
          const local = oppEnLocal(opp, getDolarValue, dolarFallback[pais]);
          const importeLocal = parseFloat(local.importe) || 0;
          const neto = calcularImporteNetoIntermediario(
            local,
            Object.values(clientes),
            Object.values(intermediarios),
            pais
          );
          const comision = importeLocal - neto;
          const porcentaje = calcularPorcentajeComisionIntermediario(
            opp,
            Object.values(clientes),
            Object.values(intermediarios),
            pais
          );
          const intermediario = obtenerNombreIntermediario(
            opp,
            Object.values(clientes),
            Object.values(intermediarios),
            pais
          );

          comisiones[monthIndex].push({
            intermediario,
            facturanumero: opp.facturanumero,
            comision,
            porcentaje,
            importe: importeLocal,
            importeOriginalMoneda: parseFloat(opp.importe) || 0,
            moneda_dolar: !!opp.moneda_dolar,
            oppId: opp.id,
          });
        }
      });
    return comisiones;
  }, [oportunidades, clientes, intermediarios, monthsToShow, selectedYear, pais, getDolarValue, dolarFallback]);

  const valoresMensualesComisiones = useMemo(() => {
    if (isOportunidadesLoading) return Array(12).fill(0);
    const valores = Array(12).fill(0);
    oportunidades
      .filter((opp) => opp.estatus === "100%" && !opp.eliminada && opp.facturafecha && !opp.isBTL)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        if (monthIndex >= 0 && monthIndex < monthsToShow && facturaDate.getFullYear() === selectedYear) {
          const local = oppEnLocal(opp, getDolarValue, dolarFallback[pais]);
          const netoFactura = calcularImporteNetoIntermediario(
            local,
            Object.values(clientes),
            Object.values(intermediarios),
            pais
          );
          valores[monthIndex] += calcularComisionEdificiosTotal(local, edificios, netoFactura);
        }
      });
    return valores;
  }, [
    oportunidades,
    edificios,
    clientes,
    intermediarios,
    pais,
    monthsToShow,
    selectedYear,
    isOportunidadesLoading,
    getDolarValue,
    dolarFallback,
  ]);

  // Comisión Edificios AÑO ANTERIOR (para EBITDA Run Rate)
  const valoresMensualesComisionesAnoAnterior = useMemo(() => {
    if (isOportunidadesLoading) return Array(12).fill(0);
    const valores = Array(12).fill(0);
    const anoAnterior = selectedYear - 1;
    oportunidades
      .filter((opp) => opp.estatus === "100%" && !opp.eliminada && opp.facturafecha && !opp.isBTL)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        if (monthIndex >= 0 && facturaDate.getFullYear() === anoAnterior) {
          const local = oppEnLocal(opp, getDolarValue, dolarFallback[pais]);
          const netoFactura = calcularImporteNetoIntermediario(
            local,
            Object.values(clientes),
            Object.values(intermediarios),
            pais
          );
          valores[monthIndex] += calcularComisionEdificiosTotal(local, edificios, netoFactura);
        }
      });
    return valores;
  }, [
    oportunidades,
    edificios,
    clientes,
    intermediarios,
    pais,
    selectedYear,
    isOportunidadesLoading,
    getDolarValue,
    dolarFallback,
  ]);

  // === COMISIÓN EDIFICIOS FIJA CHILE ===
  const valoresMensualesComisionesChile = useMemo(() => {
    const valores = Array(12).fill(0);
    oportunidadesChile
      .filter((opp) => opp.estatus === "100%" && !opp.eliminada && opp.facturafecha && !opp.isBTL)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        if (monthIndex >= 0 && monthIndex < 12 && facturaDate.getFullYear() === selectedYear) {
          const local = oppEnLocal(opp, getDolarValueChile, dolarFallback.Chile);
          const netoFactura = calcularImporteNetoIntermediario(
            local,
            Object.values(clientesChile),
            Object.values(intermediariosChile),
            "Chile"
          );
          valores[monthIndex] += calcularComisionEdificiosTotal(local, edificiosChile, netoFactura);
        }
      });
    return valores;
  }, [
    oportunidadesChile,
    edificiosChile,
    clientesChile,
    intermediariosChile,
    selectedYear,
    getDolarValueChile,
    dolarFallback,
  ]);

  // === COMISIÓN EDIFICIOS FIJA PERÚ ===
  const valoresMensualesComisionesPeru = useMemo(() => {
    const valores = Array(12).fill(0);
    oportunidadesPeru
      .filter((opp) => opp.estatus === "100%" && !opp.eliminada && opp.facturafecha && !opp.isBTL)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        if (monthIndex >= 0 && monthIndex < 12 && facturaDate.getFullYear() === selectedYear) {
          const local = oppEnLocal(opp, getDolarValuePeru, dolarFallback["Perú"]);
          const netoFactura = calcularImporteNetoIntermediario(
            local,
            Object.values(clientesPeru),
            Object.values(intermediariosPeru),
            "Perú"
          );
          valores[monthIndex] += calcularComisionEdificiosTotal(local, edificiosPeru, netoFactura);
        }
      });
    return valores;
  }, [
    oportunidadesPeru,
    edificiosPeru,
    clientesPeru,
    intermediariosPeru,
    selectedYear,
    getDolarValuePeru,
    dolarFallback,
  ]);

  const comisionesEdificiosPorMes = useMemo(() => {
    if (isOportunidadesLoading) {
      return Array(12)
        .fill([])
        .map(() => []);
    }
    const comisiones = Array(12)
      .fill([])
      .map(() => []);
    oportunidades
      .filter((opp) => opp.estatus === "100%" && !opp.eliminada && opp.facturafecha && !opp.isBTL)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        if (monthIndex >= 0 && monthIndex < monthsToShow && facturaDate.getFullYear() === selectedYear) {
          const local = oppEnLocal(opp, getDolarValue, dolarFallback[pais]);
          const netoFactura = calcularImporteNetoIntermediario(
            local,
            Object.values(clientes),
            Object.values(intermediarios),
            pais
          );
          const totalComision = calcularComisionEdificiosTotal(local, edificios, netoFactura);
          const cliente = opp.cliente || "Sin cliente";
          // Detectar si todos los edificios participantes son fijos (para mostrar etiqueta en modal)
          const fecha = resolverFechaFactura(opp);
          const edIds = opp.edificiosparticipantes
            ? [...new Set(opp.edificiosparticipantes.split(",").map(Number))]
            : [];
          const soloFijos =
            edIds.length > 0 &&
            edIds.every((id) => {
              const ed = edificios[id];
              return ed && esEdificioComisionFija(ed, fecha);
            });
          comisiones[monthIndex].push({
            facturanumero: opp.facturanumero,
            cliente,
            totalComision,
            oppId: opp.id,
            soloFijos,
            sinEdificios: edIds.length === 0,
          });
        }
      });
    return comisiones;
  }, [
    oportunidades,
    edificios,
    clientes,
    intermediarios,
    pais,
    monthsToShow,
    selectedYear,
    isOportunidadesLoading,
    getDolarValue,
    dolarFallback,
  ]);

  const valoresMensualesComisionComercial = useMemo(() => {
    if (isOportunidadesLoading)
      return {
        valores: Array(12).fill(0),
        comisionesPorOportunidad: {},
      };
    const valores = Array(12).fill(0);
    const comisionesPorOportunidad = {};

    oportunidades
      .filter((opp) => {
        if (opp.estatus !== "100%" || opp.eliminada || !opp.facturafecha) return false;
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return false;
        const monthIndex = facturaDate.getMonth();
        const facturaYear = facturaDate.getFullYear();
        return monthIndex >= 0 && monthIndex < monthsToShow && facturaYear === selectedYear;
      })
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        const monthIndex = facturaDate.getMonth();
        const oppId = opp.id;
        const local = oppEnLocal(opp, getDolarValue, dolarFallback[pais]);
        const importeLocal = parseFloat(local.importe) || 0;
        const comisionComercial = calculateComission.calculateSalesCommission(
          local,
          clientes,
          intermediarios,
          edificios
        );
        const comisionAgencia = calculateComission.calculateAgencyCommission(local, clientes, intermediarios);
        const netoAgencia = calcularImporteNetoIntermediario(
          local,
          Object.values(clientes),
          Object.values(intermediarios),
          pais
        );
        const comisionEdificios = calcularComisionEdificiosTotal(local, edificios, netoAgencia);
        comisionesPorOportunidad[oppId] = {
          facturanumero: opp.facturanumero,
          importe: importeLocal,
          importeOriginalMoneda: parseFloat(opp.importe) || 0,
          moneda_dolar: !!opp.moneda_dolar,
          ejecutivo: opp.ejecutivo ? users[opp.ejecutivo] || "Sin Ejecutivo" : "Sin Ejecutivo",
          comisionComercial,
          comisionAgencia,
          comisionEdificios,
          neto: importeLocal - comisionAgencia - comisionEdificios,
          desglose: calculateComission.getSalesCommissionBreakdown(local, clientes, intermediarios, edificios),
        };
        valores[monthIndex] += comisionComercial;
      });

    return { valores, comisionesPorOportunidad };
  }, [
    oportunidades,
    clientes,
    intermediarios,
    edificios,
    users,
    monthsToShow,
    selectedYear,
    isOportunidadesLoading,
    getDolarValue,
    dolarFallback,
    pais,
  ]);

  // Comisión Comercial AÑO ANTERIOR (para EBITDA Run Rate)
  const valoresMensualesComisionComercialAnoAnterior = useMemo(() => {
    if (isOportunidadesLoading) return Array(12).fill(0);
    const valores = Array(12).fill(0);
    const anoAnterior = selectedYear - 1;

    oportunidades
      .filter((opp) => {
        if (opp.estatus !== "100%" || opp.eliminada || !opp.facturafecha) return false;
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return false;
        const facturaYear = facturaDate.getFullYear();
        return facturaYear === anoAnterior;
      })
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        const monthIndex = facturaDate.getMonth();
        const local = oppEnLocal(opp, getDolarValue, dolarFallback[pais]);
        valores[monthIndex] += calculateComission.calculateSalesCommission(local, clientes, intermediarios, edificios);
      });

    return valores;
  }, [
    oportunidades,
    clientes,
    intermediarios,
    edificios,
    selectedYear,
    isOportunidadesLoading,
    getDolarValue,
    dolarFallback,
    pais,
  ]);

  // === COMISIÓN COMERCIAL FIJA CHILE ===
  const valoresMensualesComisionComercialChile = useMemo(() => {
    const valores = Array(12).fill(0);
    oportunidadesChile
      .filter((opp) => {
        if (opp.estatus !== "100%" || opp.eliminada || !opp.facturafecha) return false;
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return false;
        const monthIndex = facturaDate.getMonth();
        const facturaYear = facturaDate.getFullYear();
        return monthIndex >= 0 && monthIndex < 12 && facturaYear === selectedYear;
      })
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        const monthIndex = facturaDate.getMonth();
        const local = oppEnLocal(opp, getDolarValueChile, dolarFallback.Chile);
        valores[monthIndex] += calculateComission.calculateSalesCommission(
          local,
          clientesChile,
          intermediariosChile,
          edificiosChile
        );
      });
    return valores;
  }, [
    oportunidadesChile,
    clientesChile,
    intermediariosChile,
    edificiosChile,
    selectedYear,
    getDolarValueChile,
    dolarFallback,
  ]);

  // === COMISIÓN COMERCIAL FIJA PERÚ ===
  const valoresMensualesComisionComercialPeru = useMemo(() => {
    const valores = Array(12).fill(0);
    oportunidadesPeru
      .filter((opp) => {
        if (opp.estatus !== "100%" || opp.eliminada || !opp.facturafecha) return false;
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return false;
        const monthIndex = facturaDate.getMonth();
        const facturaYear = facturaDate.getFullYear();
        return monthIndex >= 0 && monthIndex < 12 && facturaYear === selectedYear;
      })
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        const monthIndex = facturaDate.getMonth();
        const local = oppEnLocal(opp, getDolarValuePeru, dolarFallback["Perú"]);
        valores[monthIndex] += calculateComission.calculateSalesCommission(
          local,
          clientesPeru,
          intermediariosPeru,
          edificiosPeru
        );
      });
    return valores;
  }, [
    oportunidadesPeru,
    clientesPeru,
    intermediariosPeru,
    edificiosPeru,
    selectedYear,
    getDolarValuePeru,
    dolarFallback,
  ]);

  const ytdComisionAgencia = useMemo(
    () => valoresMensualesComisionAgencia.slice(0, monthsToShow).reduce((acc, val) => acc + val, 0),
    [valoresMensualesComisionAgencia, monthsToShow]
  );

  const ytdComisiones = useMemo(
    () => valoresMensualesComisiones.slice(0, monthsToShow).reduce((acc, val) => acc + val, 0),
    [valoresMensualesComisiones, monthsToShow]
  );

  const ytdComisionComercial = useMemo(
    () => valoresMensualesComisionComercial.valores.slice(0, monthsToShow).reduce((acc, val) => acc + val, 0),
    [valoresMensualesComisionComercial, monthsToShow]
  );

  return {
    valoresMensuales,
    valoresMensualesAnoAnterior,
    valoresMensualesChile,
    valoresMensualesPeru,
    valoresMensualesOtroPais,
    publicidadMensualesCombinados,
    facturasPorMes,
    valoresMensualesComisionAgencia,
    valoresMensualesComisionAgenciaAnoAnterior,
    valoresMensualesComisionAgenciaChile,
    valoresMensualesComisionAgenciaPeru,
    comisionesAgenciaPorMes,
    valoresMensualesComisiones,
    valoresMensualesComisionesAnoAnterior,
    valoresMensualesComisionesChile,
    valoresMensualesComisionesPeru,
    comisionesEdificiosPorMes,
    valoresMensualesComisionComercial,
    valoresMensualesComisionComercialAnoAnterior,
    valoresMensualesComisionComercialChile,
    valoresMensualesComisionComercialPeru,
    ytdComisionAgencia,
    ytdComisiones,
    ytdComisionComercial,
  };
}
